export type ExpenseCategory = 
  | 'bol'
  | 'travel'
  | 'fuel'
  | 'truck'        // NEW: Own truck expenses (hotshot/towaway)
  | 'tolls'        // NEW: Tolls and road fees
  | 'settlements'
  | 'meals'
  | 'office'
  | 'miscellaneous'
  | 'statements';

export type TravelSubcategory = 
  | 'uber'
  | 'lyft'
  | 'rental'
  | 'hotel'
  | 'shuttle'
  | 'airfare'
  | 'parking'
  | 'greyhound'    // NEW: Bus travel
  | 'train'        // NEW: Amtrak, etc.
  | 'taxi'         // NEW: Traditional taxi
  | 'other';

// NEW: Subcategories for truck expenses (own vehicle)
export type TruckSubcategory =
  | 'repairs'        // Major repairs, breakdowns
  | 'maintenance'    // Oil changes, routine service
  | 'parts'          // Wheels, filters, belts, etc.
  | 'fluids'         // Antifreeze, DEF, oil
  | 'tires'          // Tires, tire repairs
  | 'loan_interest'  // Vehicle loan interest (Navy Federal)
  | 'insurance'      // Commercial truck insurance
  | 'registration'   // Tags, permits, DOT fees
  | 'wash'           // Truck wash
  | 'other';

// NEW: Subcategories for fuel
export type FuelSubcategory =
  | 'own_truck'      // Diesel for YOUR truck (hotshot/towaway)
  | 'delivery'       // Fuel for vehicles being delivered
  | 'def'            // Diesel Exhaust Fluid
  | 'other';

// Source tracking - where did this expense come from?
export type ExpenseSource =
  | 'manual'           // Manually entered via Upload page
  | 'bank_statement'   // Imported from bank/credit card statement
  | 'loves_receipt'    // Love's fuel receipt (PDF/scan)
  | 'loves_portal'     // Love's My Love Rewards portal export
  | 'settlement'       // Settlement statement
  | 'uber_receipt'     // Uber receipt
  | 'lyft_receipt'     // Lyft receipt
  | 'hotel_receipt'    // Hotel receipt
  | 'rental_receipt'   // Car rental receipt
  | 'scanned'          // Camera scanned document
  | 'other';

export interface Expense {
  id: string;
  category: ExpenseCategory;
  subcategory?: TravelSubcategory | TruckSubcategory | FuelSubcategory; // Supports all subcategory types
  description: string;
  amount?: number;
  date: string;
  month: number; // 1-12
  year: number;
  day?: number; // 1-31 (for easier date comparisons)
  filename: string;
  fileData: string; // base64 encoded
  fileType: string;
  fileSize: number;
  createdAt: string;
  freightBillNumber?: string; // For BOLs
  isSentToBennett?: boolean; // Track if BOL was emailed
  // Settlement/Trip data
  miles?: number; // Trip miles from settlement
  origin?: string; // Trip origin city/state
  destination?: string; // Trip destination city/state
  company?: string; // Transport company (Bennett, Auto Driveaway, etc.)
  // Truck expense tracking (for loan interest, etc.)
  vendor?: string; // Jiffy Lube, Navy Federal, etc.
  vehicleInfo?: string; // "2014 RAM 3500" etc.
  // Source tracking for duplicate detection
  source?: ExpenseSource; // Where this expense came from
  storeNumber?: string; // Store/location ID (Love's 674, Pilot 1049, etc.)
  cardLast4?: string; // Last 4 digits of payment card
  transactionId?: string; // Unique transaction/invoice/auth number
}

export interface DriverSettings {
  driverName: string;
  truckNumber: string;
  driverId: string;
  bennettEmail: string;
  cpaEmail: string;
  reminderDay: number; // Day of month for CPA reminder
}

export interface Stats {
  totalExpenses: number;
  totalAmount: number;
  byCategory: Record<ExpenseCategory, number>;
  byMonth: Record<string, number>;
  storageUsed: number;
  storageUsedFormatted: string;
}

export const CATEGORIES: { value: ExpenseCategory; label: string; icon: string; color: string }[] = [
  { value: 'bol', label: 'Bills of Lading', icon: '📋', color: 'blue' },
  { value: 'travel', label: 'Travel', icon: '✈️', color: 'purple' },
  { value: 'fuel', label: 'Fuel', icon: '⛽', color: 'orange' },
  { value: 'truck', label: 'Truck/Vehicle', icon: '🚛', color: 'red' },        // NEW
  { value: 'tolls', label: 'Tolls & Fees', icon: '🛣️', color: 'indigo' },      // NEW
  { value: 'settlements', label: 'Settlements', icon: '💰', color: 'green' },
  { value: 'meals', label: 'Meals', icon: '🍔', color: 'yellow' },
  { value: 'office', label: 'Office Supplies', icon: '📎', color: 'pink' },
  { value: 'miscellaneous', label: 'Miscellaneous', icon: '📦', color: 'gray' },
  { value: 'statements', label: 'Statements', icon: '📄', color: 'teal' },
];

export const TRAVEL_SUBCATEGORIES: { value: TravelSubcategory; label: string; icon: string }[] = [
  { value: 'uber', label: 'Uber', icon: '🚗' },
  { value: 'lyft', label: 'Lyft', icon: '🚕' },
  { value: 'rental', label: 'Rental Car', icon: '🚙' },
  { value: 'hotel', label: 'Hotel', icon: '🏨' },
  { value: 'shuttle', label: 'Shuttle', icon: '🚐' },
  { value: 'airfare', label: 'Airfare', icon: '✈️' },
  { value: 'parking', label: 'Parking', icon: '🅿️' },
  { value: 'greyhound', label: 'Greyhound/Bus', icon: '🚌' },  // NEW
  { value: 'train', label: 'Train/Amtrak', icon: '🚂' },       // NEW
  { value: 'taxi', label: 'Taxi', icon: '🚖' },                // NEW
  { value: 'other', label: 'Other', icon: '📍' },
];

// NEW: Subcategories for truck/vehicle expenses
export const TRUCK_SUBCATEGORIES: { value: TruckSubcategory; label: string; icon: string }[] = [
  { value: 'repairs', label: 'Repairs', icon: '🔧' },
  { value: 'maintenance', label: 'Maintenance', icon: '🛠️' },
  { value: 'parts', label: 'Parts', icon: '⚙️' },
  { value: 'fluids', label: 'Fluids', icon: '🧴' },
  { value: 'tires', label: 'Tires', icon: '🛞' },
  { value: 'loan_interest', label: 'Loan Interest', icon: '💳' },
  { value: 'insurance', label: 'Insurance', icon: '🛡️' },
  { value: 'registration', label: 'Registration/Permits', icon: '📝' },
  { value: 'wash', label: 'Truck Wash', icon: '🧼' },
  { value: 'other', label: 'Other', icon: '📦' },
];

// NEW: Subcategories for fuel
export const FUEL_SUBCATEGORIES: { value: FuelSubcategory; label: string; icon: string }[] = [
  { value: 'own_truck', label: 'Own Truck Diesel', icon: '🚛' },
  { value: 'delivery', label: 'Delivery Vehicle', icon: '🚗' },
  { value: 'def', label: 'DEF', icon: '💧' },
  { value: 'other', label: 'Other', icon: '⛽' },
];

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const BENNETT_EMAILS = [
  { code: 'imaging', label: 'Imaging (Default)', email: 'imaging@bennettig.com' },
  { code: 'drd', label: 'DRD', email: 'drd@bennettig.com' },
  { code: 'stx', label: 'STX', email: 'stx@bennettig.com' },
  { code: 'jnc', label: 'JNC', email: 'jnc@bennettig.com' },
  { code: 'mcd', label: 'MCD', email: 'mcd@bennettig.com' },
  { code: 'ptp', label: 'PTP', email: 'ptp@bennettig.com' },
  { code: 'wgo', label: 'WGO', email: 'wgo@bennettig.com' },
  { code: 'mdn', label: 'MDN', email: 'mdn@bennettig.com' },
  { code: 'mca', label: 'MCA', email: 'mca@bennettig.com' },
  { code: 'dcm', label: 'DCM', email: 'dcm@bennettig.com' },
  { code: 'poh', label: 'POH', email: 'poh@bennettig.com' },
];

export const DEFAULT_SETTINGS: DriverSettings = {
  driverName: 'Douglas Sullivan',
  truckNumber: '582826',
  driverId: '130583',
  bennettEmail: 'imaging@bennettig.com',
  cpaEmail: '',
  reminderDay: 7, // Reminder on the 7th for docs due on 8th
};

// Get local date in YYYY-MM-DD format (fixes timezone issues)
export const getLocalDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ============================================
// SMART DUPLICATE DETECTION
// ============================================

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  confidence: 'high' | 'medium' | 'low';
  matchedExpense?: Expense;
  reasons: string[];
}

// Normalize vendor names for comparison
const normalizeVendor = (vendor: string): string => {
  return vendor
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove special chars
    .replace(/loves|love's/g, 'loves')
    .replace(/pilot|flying\s*j/g, 'pilot')
    .replace(/ta\s*petro|tapetro/g, 'tapetro')
    .replace(/jiffy\s*lube/g, 'jiffylube')
    .replace(/navy\s*federal/g, 'navyfederal');
};

// Extract store number from description
const extractStoreNumber = (text: string): string | null => {
  // Match patterns like "STORE 674", "#674", "Store #674", "LOVES 674"
  const patterns = [
    /store\s*#?\s*(\d+)/i,
    /loves?\s*#?\s*(\d+)/i,
    /pilot\s*#?\s*(\d+)/i,
    /#(\d{3,5})/,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Check if two dates are within N days of each other
const datesWithinRange = (date1: string, date2: string, days: number): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffMs = Math.abs(d1.getTime() - d2.getTime());
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= days;
};

// Smart duplicate detection
export const checkForDuplicates = (
  newExpense: {
    amount?: number;
    date: string;
    description?: string;
    vendor?: string;
    storeNumber?: string;
    cardLast4?: string;
    transactionId?: string;
    category?: ExpenseCategory;
  },
  existingExpenses: Expense[]
): DuplicateCheckResult => {
  const results: DuplicateCheckResult = {
    isDuplicate: false,
    confidence: 'low',
    reasons: [],
  };

  if (!newExpense.amount || !newExpense.date) {
    return results;
  }

  const newStoreNum = newExpense.storeNumber || 
    (newExpense.description ? extractStoreNumber(newExpense.description) : null);
  
  const newVendorNorm = newExpense.vendor ? normalizeVendor(newExpense.vendor) : 
    (newExpense.description ? normalizeVendor(newExpense.description) : '');

  for (const existing of existingExpenses) {
    const matchReasons: string[] = [];
    let score = 0;

    // 1. TRANSACTION ID MATCH (definite duplicate)
    if (newExpense.transactionId && existing.transactionId && 
        newExpense.transactionId === existing.transactionId) {
      return {
        isDuplicate: true,
        confidence: 'high',
        matchedExpense: existing,
        reasons: ['Same transaction/invoice ID'],
      };
    }

    // 2. EXACT AMOUNT MATCH (within $0.02)
    if (existing.amount && Math.abs(existing.amount - newExpense.amount) < 0.02) {
      matchReasons.push(`Same amount: $${existing.amount.toFixed(2)}`);
      score += 3;
    } else {
      continue; // Different amounts = not a duplicate
    }

    // 3. DATE MATCH (exact or within 1 day for bank posting delays)
    if (existing.date === newExpense.date) {
      matchReasons.push(`Same date: ${existing.date}`);
      score += 3;
    } else if (datesWithinRange(existing.date, newExpense.date, 1)) {
      matchReasons.push(`Date within 1 day: ${existing.date} vs ${newExpense.date}`);
      score += 2;
    } else if (datesWithinRange(existing.date, newExpense.date, 3)) {
      matchReasons.push(`Date within 3 days: ${existing.date} vs ${newExpense.date}`);
      score += 1;
    } else {
      continue; // Dates too far apart = not a duplicate
    }

    // 4. STORE NUMBER MATCH - This is CRITICAL for fuel stops!
    const existingStoreNum = existing.storeNumber || 
      (existing.description ? extractStoreNumber(existing.description) : null);
    
    if (newStoreNum && existingStoreNum) {
      if (newStoreNum === existingStoreNum) {
        // SAME store number = almost certainly the same transaction!
        matchReasons.push(`✓ SAME store: #${existingStoreNum}`);
        score += 6; // High weight - this is strong evidence
      } else {
        // DIFFERENT store numbers = likely NOT a duplicate, reduce score
        matchReasons.push(`Different stores: #${newStoreNum} vs #${existingStoreNum}`);
        score -= 2; // Penalty for different stores
      }
    }

    // 5. VENDOR/DESCRIPTION SIMILARITY
    const existingVendorNorm = existing.vendor ? normalizeVendor(existing.vendor) : 
      (existing.description ? normalizeVendor(existing.description) : '');
    
    if (newVendorNorm && existingVendorNorm) {
      if (newVendorNorm === existingVendorNorm) {
        matchReasons.push('Same vendor');
        score += 2;
      } else if (newVendorNorm.includes(existingVendorNorm) || existingVendorNorm.includes(newVendorNorm)) {
        matchReasons.push('Similar vendor name');
        score += 1;
      }
    }

    // 6. CARD LAST 4 MATCH
    if (newExpense.cardLast4 && existing.cardLast4 && 
        newExpense.cardLast4 === existing.cardLast4) {
      matchReasons.push(`Same card: ****${existing.cardLast4}`);
      score += 2;
    }

    // 7. SAME CATEGORY
    if (newExpense.category && existing.category === newExpense.category) {
      matchReasons.push(`Same category: ${existing.category}`);
      score += 1;
    }

    // Determine confidence based on score
    // Always track the BEST match (highest score) instead of returning first match
    const currentBestScore = results.matchedExpense ? 
      (results.confidence === 'high' ? 10 : results.confidence === 'medium' ? 6 : 4) : 0;
    
    if (score >= 9) {
      // Very high score (has store number match) = HIGH confidence
      if (score > currentBestScore) {
        results.isDuplicate = true;
        results.confidence = 'high';
        results.matchedExpense = existing;
        results.reasons = matchReasons;
      }
    } else if (score >= 6) {
      // Good match = MEDIUM confidence
      if (score > currentBestScore) {
        results.isDuplicate = true;
        results.confidence = 'medium';
        results.matchedExpense = existing;
        results.reasons = matchReasons;
      }
    } else if (score >= 4) {
      // Weak match = LOW confidence
      if (score > currentBestScore) {
        results.isDuplicate = true;
        results.confidence = 'low';
        results.matchedExpense = existing;
        results.reasons = matchReasons;
      }
    }
    // If score < 4 (e.g., different stores penalty dropped it), skip this candidate
  }

  return results;
};

// Format duplicate warning message
export const formatDuplicateWarning = (result: DuplicateCheckResult): string => {
  if (!result.isDuplicate || !result.matchedExpense) return '';
  
  const exp = result.matchedExpense;
  const confidenceEmoji = result.confidence === 'high' ? '🚨' : 
                          result.confidence === 'medium' ? '⚠️' : '❓';
  
  const sourceLabel = exp.source ? ` (from ${exp.source.replace(/_/g, ' ')})` : '';
  const filenameLabel = exp.filename ? `\n📄 File: ${exp.filename}` : '';
  const idLabel = exp.id ? `\n🔑 ID: ${exp.id.slice(0, 8)}...` : '';
  
  return `${confidenceEmoji} ${result.confidence.toUpperCase()} confidence duplicate:\n` +
    `${exp.description || exp.category}${sourceLabel}\n` +
    `$${exp.amount?.toFixed(2)} on ${exp.date}` +
    filenameLabel + idLabel + `\n\n` +
    `Matches: ${result.reasons.join(', ')}`;
};





